import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';

const LETRAS = ['S', 'M', 'A', 'R', 'T'] as const;

// D-044 (19/10/2026, pedido de Javier): un botoncito de info pegado al
// objetivo para que Ailen aprenda a escribir objetivos SMART. Se abre al pasar
// el mouse y, si hace clic, queda abierto para leerlo tranquila; se cierra con
// otro clic, con Escape o haciendo clic afuera.
export function InfoSmart(): JSX.Element {
  const es = useI18n();
  const t = es.tareas.objetivo.smart;
  const [hover, setHover] = useState(false);
  const [fijo, setFijo] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const abierto = hover || fijo;

  useEffect(() => {
    if (!fijo) return undefined;
    function alHacerClic(e: MouseEvent): void {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setFijo(false);
    }
    function alTeclear(e: KeyboardEvent): void {
      if (e.key === 'Escape') setFijo(false);
    }
    document.addEventListener('mousedown', alHacerClic);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alHacerClic);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [fijo]);

  return (
    <div
      ref={raiz}
      className="relative shrink-0"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        aria-label={t.boton}
        aria-expanded={abierto}
        aria-controls="info-smart"
        onClick={() => setFijo((f) => !f)}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full transition-colors',
          abierto ? 'text-marca-primario' : 'text-text-faint hover:text-marca-primario',
        )}
      >
        <Info size={18} />
      </button>
      {abierto && (
        <div
          id="info-smart"
          role="tooltip"
          className="absolute left-0 top-full z-30 mt-2 w-[22rem] rounded-xl border border-border-subtle bg-surface p-4 text-sm shadow-card-hover"
        >
          <p className="mb-2 font-semibold text-text-marca">{t.titulo}</p>
          <dl className="flex flex-col gap-1.5">
            {LETRAS.map((letra) => (
              <div key={letra} className="flex gap-2">
                <dt className="w-5 shrink-0 font-bold text-marca-primario">{letra}</dt>
                <dd className="text-text-subtle">
                  <span className="font-medium text-text-strong">{t[letra].nombre}.</span> {t[letra].detalle}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-text-subtle">
            {t.ejemploTitulo}
          </p>
          <p className="text-text-faint">{t.ejemploMal}</p>
          <p className="text-text-strong">{t.ejemploBien}</p>
        </div>
      )}
    </div>
  );
}
